import { displayText, clientUserText } from '../../../../../terminology/store';
import { useTerminology } from '../../../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import ThemeContext from '../../../../ThemeContext';
import {
  createDeposition,
  updateDeposition,
  getDeposition,
  getCsrfToken,
  getCase,
  getResellers,
} from '../../../../services/api';
import { validateField, validateForm } from '../../../../Utils/SessionsValidationsSchema';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import ClientConfig from '../../../../../config/Clientconfig';
import { URL_SEGMENTS } from '../../../../../config/Routesconfig';

//  shorthand for this session type
const SC = ClientConfig.sessions.types.deposition;

const AddDeposition = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const { username, id: paramCaseId, depositionId } = useParams();
  const navigate = useNavigate();

  const isEditMode = Boolean(depositionId);

  const generatePasscode = () =>
    Math.floor(10000 + Math.random() * 90000).toString();

  const [caseId, setCaseId] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [combinedUsers, setCombinedUsers] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingResellers, setLoadingResellers] = useState(false);

  // ---- touched: only show introducing_party error after interaction ----
  const [touched, setTouched] = useState({});

  // ---- search state for introducing party ----
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  const getInitialFormData = () => ({
    witness: '',
    volume: '',
    title: '',
    introducing_party: [],
    date: '',
    details: '',
    court_reporting_firm: '',
    location: '',
    session_passcode: generatePasscode(),

    schedule_video: false,
    zoom_tech: '',
    registrants: '',
    meeting_duration: '60',
    meeting_time: '',
    meeting_timezone: '',

    // optional stored zoom meeting fields
    zoom_meeting_id: '',
    zoom_join_url: '',
    zoom_start_url: '',
    zoom_password: '',
    zoom_created_at: '',
  });

  const [formData, setFormData] = useState(getInitialFormData);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
        // Mark touched when user clicks away
        setTouched((prev) => ({ ...prev, introducing_party: true }));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ================= CSRF & DATA FETCHING ================= */
  useEffect(() => {
    getCsrfToken();

    const fetchCombinedUsers = async () => {
      setLoadingUsers(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/webmanager/client-users/combined-for-introducing`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
            },
            withCredentials: true,
          }
        );

        if (response.data.success) {
          setCombinedUsers(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching combined users:', error);
        toast.error('Failed to load users');
      } finally {
        setLoadingUsers(false);
      }
    };

    const fetchResellers = async () => {
      setLoadingResellers(true);
      try {
        const response = await getResellers();
        if (response.data) {
          setResellers(response.data.data || response.data || []);
        }
      } catch (error) {
        console.error('Error fetching resellers:', error);
        toast.error('Failed to load court reporting firms');
      } finally {
        setLoadingResellers(false);
      }
    };

    fetchCombinedUsers();
    fetchResellers();
  }, []);

  /* ================= LOAD CASE + DEPOSITION (EDIT MODE) ================= */
  useEffect(() => {
    if (!paramCaseId || isNaN(paramCaseId)) {
      toast.error(displayText('Invalid case ID'));
      navigate(`/${username}/dashboard/cases`);
      return;
    }

    setCaseId(parseInt(paramCaseId, 10));

    if (isEditMode) {
      const loadDeposition = async () => {
        try {
          const res = await getDeposition(depositionId);
          const deposition = res.data?.data || res.data;

          if (!deposition?.id) {
            throw new Error('Invalid deposition data');
          }

          if (Number(deposition.case_id) !== Number(paramCaseId)) {
            throw new Error('Deposition does not belong to this case');
          }

          setFormData({
            witness: deposition.witness || '',
            volume: deposition.volume || '',
            title: deposition.title || '',
            introducing_party: deposition.introducing_party
              ? deposition.introducing_party.split(',').map((p) => p.trim())
              : [],
            date: deposition.date || '',
            details: deposition.details || '',
            court_reporting_firm: deposition.court_reporting_firm || '',
            location: deposition.location || '',
            session_passcode: deposition.session_passcode || generatePasscode(),

            schedule_video: !!deposition.schedule_video,
            zoom_tech: deposition.zoom_tech || (deposition.schedule_video ? 'zoom' : ''),
            registrants: deposition.registrants || '',
            meeting_duration: deposition.meeting_duration || '60',
            meeting_time: deposition.meeting_time || '',
            meeting_timezone: deposition.meeting_timezone || '',

            zoom_meeting_id: deposition.zoom_meeting_id || '',
            zoom_join_url: deposition.zoom_join_url || '',
            zoom_start_url: deposition.zoom_start_url || '',
            zoom_password: deposition.zoom_password || deposition.session_passcode || '',
            zoom_created_at: deposition.zoom_created_at || '',
          });
        } catch (err) {
          console.error('Load deposition error:', err);
          toast.error(
            err.response?.data?.message || err.message || displayText('Failed to load deposition data')
          );
        }
      };

      loadDeposition();
    }
  }, [paramCaseId, depositionId, isEditMode, navigate, username]);

  /* ================= FETCH CASE DETAILS ================= */
  useEffect(() => {
    if (!caseId) return;

    const fetchCase = async () => {
      try {
        const res = await getCase(caseId);
        setCaseData(res.data.data);
      } catch (err) {
        toast.error(displayText('Failed to fetch case details'));
        navigate(`/${username}/dashboard/cases`);
      }
    };

    fetchCase();
  }, [caseId, navigate, username]);

  /* ================= HANDLERS ================= */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target || {};

    if (name === 'schedule_video') {
      const isEnabled = checked;

      setFormData((prev) => ({
        ...prev,
        schedule_video: isEnabled,
        zoom_tech: isEnabled ? 'zoom' : '',
        registrants: isEnabled ? prev.registrants : '',
        meeting_duration: isEnabled ? (prev.meeting_duration || '60') : '60',
        meeting_time: isEnabled ? prev.meeting_time : '',
        meeting_timezone: isEnabled ? prev.meeting_timezone : '',

        zoom_meeting_id: isEnabled ? prev.zoom_meeting_id : '',
        zoom_join_url: isEnabled ? prev.zoom_join_url : '',
        zoom_start_url: isEnabled ? prev.zoom_start_url : '',
        zoom_password: isEnabled ? (prev.session_passcode || '') : '',
        zoom_created_at: isEnabled ? prev.zoom_created_at : '',
      }));

      setErrors((prev) => ({
        ...prev,
        zoom_tech: '',
        registrants: '',
        meeting_duration: '',
        meeting_time: '',
        meeting_timezone: '',
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = async (e) => {
    const { name } = e.target;
    await validateField(name, formData, setErrors, 'deposition');
  };

  // ---- Search-based introducing party handlers ----
  const filteredUsers = combinedUsers.filter((user) => {
    const name = (user.display_name || '').toLowerCase();
    const search = searchText.toLowerCase().trim();
    if (!search) return true;
    return name.includes(search);
  });

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setShowDropdown(true);
  };

  const handleSearchFocus = () => {
    setShowDropdown(true);
  };

  const handleSelectUser = (user) => {
    const value = user.display_name;
    if (value && !formData.introducing_party.includes(value)) {
      setFormData((prev) => ({
        ...prev,
        introducing_party: [...prev.introducing_party, value],
      }));
    }
    setSearchText('');
    setShowDropdown(false);
    // Clear error once a user is selected
    setTouched((prev) => ({ ...prev, introducing_party: false }));
  };

  const removeIntroducingParty = (partyToRemove) => {
    setFormData((prev) => {
      const updated = prev.introducing_party.filter((p) => p !== partyToRemove);
      if (updated.length === 0) {
        // Show error only when all parties are removed
        setTouched((t) => ({ ...t, introducing_party: true }));
      }
      return { ...prev, introducing_party: updated };
    });
  };

  const resetFormAndRedirect = () => {
    setFormData(getInitialFormData());
    setErrors({});
    navigate(`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId || paramCaseId}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Always mark touched on submit attempt
    setTouched((prev) => ({ ...prev, introducing_party: true }));

    if (isSubmitting) return;

    setIsSubmitting(true);
    setIsLoading(true);

    if (formData.introducing_party.length === 0) {
      toast.error('Please select at least one introducing party');
      setIsLoading(false);
      setIsSubmitting(false);
      return;
    }

    try {
      const cleanedIntroducingParty = formData.introducing_party
        .map((name) => name.replace(/^"|"$/g, '').trim())
        .filter(Boolean);

      const formDataForValidation = {
        ...formData,
        introducing_party: cleanedIntroducingParty,
      };

      const isValid = await validateForm(formDataForValidation, setErrors, 'deposition');

      if (!isValid) {
        toast.error('Please fill all required fields correctly.');
        throw new Error('Validation failed');
      }

      const payload = {
        case_id: caseId,
        session_type: 'deposition',
        witness: formData.witness,
        volume: formData.volume,
        title: formData.title,
        introducing_party: cleanedIntroducingParty,
        date: formData.date,
        details: formData.details,
        court_reporting_firm: formData.court_reporting_firm,
        location: formData.location,
        session_passcode: formData.session_passcode,
        schedule_video: formData.schedule_video,
        zoom_tech: formData.schedule_video ? 'zoom' : null,
        registrants: formData.schedule_video ? formData.registrants : null,
        meeting_duration: formData.schedule_video ? formData.meeting_duration : null,
        meeting_time: formData.schedule_video ? formData.meeting_time : null,
        meeting_timezone: formData.schedule_video ? formData.meeting_timezone : null,
      };

      console.log('Sending deposition payload:', payload);

      const response = isEditMode
        ? await updateDeposition(depositionId, payload)
        : await createDeposition(payload);

      if (response?.status === 204) {
        toast.warn(
          `${SC.label} may have been ${isEditMode ? 'updated' : 'created'}, but no content was returned.`
        );
        setTimeout(() => {
          resetFormAndRedirect();
        }, 1500);
        return;
      }

      toast.success(`${SC.label} ${isEditMode ? 'updated' : displayText('session added')} successfully`);

      setTimeout(() => {
        resetFormAndRedirect();
      }, 1000);
    } catch (err) {
      console.error('Deposition submit error:', err);

      if (err.response?.status === 422) {
        const serverErrors = err.response.data?.errors || err.response.data;
        const errorMessages = Object.values(serverErrors || {}).flat().join(', ');
        toast.error(`Validation failed: ${errorMessages || 'Please check the form'}`);
        if (serverErrors) {
          setErrors((prev) => ({ ...prev, ...serverErrors }));
        }
      } else {
        toast.error(
          err.response?.data?.message ||
            err.message ||
            `Failed to save ${SC.label.toLowerCase()}`
        );
      }
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    navigate(`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId || paramCaseId}`);
  };

  // Show error only when touched AND empty
  const showIntroducingPartyError =
    touched.introducing_party && formData.introducing_party.length === 0;

  return (
    <>
      <div style={{ padding: '0rem 1.0rem 1.0rem 0rem', margin: 0 }}>
        <nav aria-label="breadcrumb">
          <ol
            className="breadcrumb mb-0"
            style={{ fontSize: '10px', padding: 0, background: 'transparent' }}
          >
            <li className="breadcrumb-item">
              <Link
                to={`/${username}/dashboard/${URL_SEGMENTS.cases}`}
                style={{
                  color: theme.colors.primary,
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  transition: 'color 0.3s ease',
                }}
                onMouseOver={(e) => (e.target.style.color = theme.colors.primary + 'cc')}
                onMouseOut={(e) => (e.target.style.color = theme.colors.primary)}
              >
                {ClientConfig.cases.managementLabel}
              </Link>
            </li>

            <li className="breadcrumb-item">
              <Link
                to={`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId || paramCaseId}`}
                style={{
                  color: theme.colors.textPrimary,
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  transition: 'color 0.3s ease',
                }}
                onMouseOver={(e) => (e.target.style.color = theme.colors.primary)}
                onMouseOut={(e) => (e.target.style.color = theme.colors.textPrimary)}
              >
                {caseData?.case_name || 'Loading...'}
              </Link>
            </li>

            <li
              className="breadcrumb-item active"
              style={{ color: theme.colors.textPrimary, fontWeight: 'bold' }}
            >
              {isEditMode ? 'Edit' : 'Add'}: {SC.label}
            </li>
          </ol>
        </nav>
      </div>

      <div
        className="container-fluid py-4"
        style={{
          backgroundColor: theme.colors.bgPrimary,
          color: theme.colors.textPrimary,
          fontFamily:
            theme?.typography?.fontFamily || '"Roboto", "Helvetica Neue", Arial, sans-serif',
          fontSize: 'clamp(10px, 2.5vw, 11px)',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          border: `1px solid ${theme.colors.borderColor || '#ddd'}`,
          margin: '0 1.0rem 1.0rem 0',
        }}
      >
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          toastStyle={{
            backgroundColor: theme.colors.bgPrimary,
            color: theme.colors.textPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            fontSize: '10px',
            fontWeight: '500',
          }}
        />

        <div
          style={{
            borderRadius: '10px',
            border: `1px solid ${theme.colors.borderColor}`,
            overflow: 'hidden',
            backgroundColor: theme.colors.bgPrimary,
          }}
        >
          <div
            style={{
              background: theme.gradients.primary,
              color: theme.colors.textWhite,
              padding: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: window.innerWidth < 600 ? 'column' : 'row',
              gap: '12px',
            }}
          >
            <h5 style={{ fontSize: '12px', fontWeight: '600', margin: 0 }}>
              {isEditMode ? 'Edit' : 'Add'}: {SC.label}
            </h5>
            <h5 style={{ fontSize: '12px', fontWeight: '600', margin: 0 }}>
              {SC.relatedToLabel}: {caseData?.case_name || 'Loading...'}
            </h5>
          </div>

          <div style={{ padding: '16px' }}>
            <form id="addDepositionForm" onSubmit={handleSubmit}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: window.innerWidth < 600 ? 'column' : 'row',
                  gap: '24px',
                  alignItems: 'flex-start',
                }}
              >
                {/* LEFT COLUMN */}
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  {/* WITNESS */}
                  <div>
                    <label
                      htmlFor="witness"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      {SC.fieldLabel}*
                    </label>
                    <input
                      type="text"
                      id="witness"
                      name="witness"
                      value={formData.witness}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter witness name"
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.witness ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.witness ? theme.colors.danger : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        fontWeight: '100',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.witness && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: '9px',
                          fontWeight: '500',
                          marginTop: '2px',
                        }}
                      >
                        {errors.witness}
                      </div>
                    )}
                  </div>

                  {/* VOLUME */}
                  <div>
                    <label
                      htmlFor="volume"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      Volume
                    </label>
                    <input
                      type="text"
                      id="volume"
                      name="volume"
                      value={formData.volume}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter volume (optional)"
                      disabled={isSubmitting}
                      className={`form-control ${errors.volume ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.volume ? theme.colors.danger : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        fontWeight: '100',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.volume && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: '9px',
                          fontWeight: '500',
                          marginTop: '2px',
                        }}
                      >
                        {errors.volume}
                      </div>
                    )}
                  </div>

                  {/* INTRODUCING PARTY — Search-based */}
                  <div>
                    <label
                      htmlFor="introducingPartySearch"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >{clientUserText(" Introducing Party* (Client Admins & Users) ")}</label>

                    {/* Search input with dropdown */}
                    <div ref={searchRef} style={{ position: 'relative' }}>
                      <input
                        type="text"
                        id="introducingPartySearch"
                        value={searchText}
                        onChange={handleSearchChange}
                        onFocus={handleSearchFocus}
                        placeholder={loadingUsers ? 'Loading users...' : 'Search to add...'}
                        disabled={isSubmitting || loadingUsers}
                        autoComplete="off"
                        style={{
                          width: '100%',
                          border: `1px solid ${
                            showIntroducingPartyError
                              ? theme.colors.danger
                              : theme.colors.borderColor
                          }`,
                          borderRadius: '4px',
                          padding: '6px 10px',
                          fontSize: '10px',
                          fontWeight: '100',
                          backgroundColor: theme.colors.bgPrimary,
                          color: '#666',
                          outline: 'none',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                          transition: 'border-color 0.3s ease',
                          opacity: isSubmitting ? 0.6 : 1,
                          fontFamily: 'inherit',
                          cursor: isSubmitting || loadingUsers ? 'not-allowed' : 'text',
                        }}
                      />

                      {/* Dropdown — only shows when focused/typing */}
                      {showDropdown && !loadingUsers && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            backgroundColor: theme.colors.bgPrimary,
                            border: `1px solid ${theme.colors.borderColor}`,
                            borderRadius: '4px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            maxHeight: '180px',
                            overflowY: 'auto',
                          }}
                        >
                          {filteredUsers.length === 0 ? (
                            <div
                              style={{
                                padding: '8px 10px',
                                fontSize: '10px',
                                color: '#999',
                                textAlign: 'center',
                              }}
                            >
                              {searchText ? 'No users found' : 'No users available'}
                            </div>
                          ) : (
                            filteredUsers.map((user) => {
                              const alreadySelected = formData.introducing_party.includes(
                                user.display_name
                              );
                              return (
                                <div
                                  key={`${user.type}-${user.id}`}
                                  onClick={() => !alreadySelected && handleSelectUser(user)}
                                  style={{
                                    padding: '7px 10px',
                                    fontSize: '10px',
                                    cursor: alreadySelected ? 'default' : 'pointer',
                                    backgroundColor: alreadySelected ? '#f5f5f5' : 'transparent',
                                    color: alreadySelected ? '#aaa' : '#444',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    borderBottom: `1px solid ${theme.colors.borderColor}`,
                                    transition: 'background-color 0.15s ease',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!alreadySelected)
                                      e.currentTarget.style.backgroundColor = '#f0f4ff';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!alreadySelected)
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <span>{user.display_name}</span>
                                  {alreadySelected ? (
                                    <span style={{ fontSize: '9px', color: theme.colors.primary }}>
                                      ✓ Added
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: '9px', color: '#bbb' }}>
                                      {user.type === 'admin' ? 'Admin' : 'User'}
                                    </span>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>

                    {/* Selected party tags */}
                    {formData.introducing_party.length > 0 && (
                      <div
                        style={{
                          marginTop: '8px',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px',
                        }}
                      >
                        {formData.introducing_party.map((party, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              backgroundColor: theme.colors.primary,
                              color: theme.colors.textWhite,
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '9px',
                              fontWeight: '500',
                            }}
                          >
                            <span style={{ marginRight: '6px' }}>{party}</span>
                            <button
                              type="button"
                              onClick={() => removeIntroducingParty(party)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: theme.colors.textWhite,
                                cursor: 'pointer',
                                fontSize: '12px',
                                padding: '0',
                                lineHeight: '1',
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Error — only shows after touched */}
                    {showIntroducingPartyError && (
                      <div
                        style={{
                          fontSize: '9px',
                          color: theme.colors.danger,
                          fontWeight: '500',
                          marginTop: '4px',
                        }}
                      >
                        Please select at least one introducing party
                      </div>
                    )}

                    {errors.introducing_party && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: '9px',
                          fontWeight: '500',
                          marginTop: '2px',
                        }}
                      >
                        {errors.introducing_party}
                      </div>
                    )}
                  </div>

                  {/* DATE */}
                  <div>
                    <label
                      htmlFor="date"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      Date*
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.date ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.date ? theme.colors.danger : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: errors.date ? '6px 32px 6px 10px' : '6px 10px',
                        fontSize: '10px',
                        fontWeight: '100',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease, padding 0.2s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.date && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: '9px',
                          fontWeight: '500',
                          marginTop: '2px',
                        }}
                      >
                        {errors.date}
                      </div>
                    )}
                  </div>

                  {/* COURT REPORTING FIRM */}
                  <div>
                    <label
                      htmlFor="courtReportingFirm"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      {SC.reportingLeadLabel}*
                    </label>
                    <select
                      id="courtReportingFirm"
                      name="court_reporting_firm"
                      value={formData.court_reporting_firm}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting || loadingResellers}
                      className={`form-control ${
                        errors.court_reporting_firm ? 'is-invalid' : ''
                      }`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.court_reporting_firm
                            ? theme.colors.danger
                            : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        fontWeight: '100',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    >
                      <option value="">
                        {loadingResellers ? 'Loading firms...' : 'Select Firm'}
                      </option>
                      {resellers.map((reseller) => {
                        const displayName =
                          reseller.company_name ||
                          reseller.name ||
                          `${reseller.first_name || ''} ${
                            reseller.last_name || ''
                          }`.trim() ||
                          reseller.username ||
                          'Unknown Firm';

                        return (
                          <option key={reseller.id} value={displayName}>
                            {displayName}
                          </option>
                        );
                      })}
                    </select>

                    {errors.court_reporting_firm && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: '9px',
                          fontWeight: '500',
                          marginTop: '2px',
                        }}
                      >
                        {errors.court_reporting_firm}
                      </div>
                    )}
                  </div>

                  {/* LOCATION */}
                  <div>
                    <label
                      htmlFor="location"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      {SC.locationLabel}
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.location ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.location ? theme.colors.danger : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        fontWeight: '100',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.location && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: '9px',
                          fontWeight: '500',
                          marginTop: '2px',
                        }}
                      >
                        {errors.location}
                      </div>
                    )}
                  </div>

                  {/* SESSION PASSCODE */}
                  <div>
                    <label
                      htmlFor="sessionPasscode"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >{displayText(" Session Passcode* (Auto-Generated) ")}</label>
                    <input
                      type="text"
                      id="sessionPasscode"
                      name="session_passcode"
                      value={formData.session_passcode}
                      readOnly
                      disabled={isSubmitting}
                      style={{
                        width: '100%',
                        border: `1px solid ${theme.colors.borderColor}`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        fontWeight: '100',
                        backgroundColor: '#f0f0f0',
                        color: '#333',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                        cursor: 'not-allowed',
                      }}
                    />
                  </div>

                  {/* DETAILS */}
                  <div>
                    <label
                      htmlFor="details"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      Details
                    </label>
                    <textarea
                      id="details"
                      name="details"
                      value={formData.details}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      rows="2"
                      disabled={isSubmitting}
                      className={`form-control ${errors.details ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.details ? theme.colors.danger : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        fontWeight: '100',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        resize: 'vertical',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.details && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: '9px',
                          fontWeight: '500',
                          marginTop: '2px',
                        }}
                      >
                        {errors.details}
                      </div>
                    )}
                  </div>
                </div>

                {/* DIVIDER */}
                <div
                  style={{
                    width: window.innerWidth < 600 ? '100%' : '1px',
                    height: window.innerWidth < 600 ? '1px' : 'auto',
                    backgroundColor: 'transparent',
                    backgroundImage:
                      window.innerWidth < 600
                        ? `repeating-linear-gradient(to right, ${theme.colors.borderColor} 0px, ${theme.colors.borderColor} 4px, transparent 4px, transparent 8px)`
                        : `repeating-linear-gradient(to bottom, ${theme.colors.borderColor} 0px, ${theme.colors.borderColor} 4px, transparent 4px, transparent 8px)`,
                    margin: window.innerWidth < 600 ? '12px 0' : '0 16px',
                    minHeight: window.innerWidth >= 600 ? '400px' : '1px',
                  }}
                />

                {/* RIGHT COLUMN */}
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  {/* SCHEDULE VIDEO TOGGLE */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <label
                      htmlFor="scheduleVideo"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        margin: 0,
                        opacity: isSubmitting ? 0.6 : 1,
                      }}
                    >
                      Schedule a Video Conference?
                    </label>

                    <div
                      style={{
                        position: 'relative',
                        width: '50px',
                        height: '24px',
                        borderRadius: '12px',
                        backgroundColor: formData.schedule_video
                          ? theme.colors.success
                          : '#ccc',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                      }}
                      onClick={() => {
                        if (!isSubmitting) {
                          const nextValue = !formData.schedule_video;
                          handleInputChange({
                            target: {
                              name: 'schedule_video',
                              type: 'checkbox',
                              checked: nextValue,
                            },
                          });
                        }
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: '2px',
                          left: formData.schedule_video ? '28px' : '2px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: theme.colors.textWhite,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          transition: 'left 0.3s ease',
                        }}
                      />
                      <input
                        type="checkbox"
                        id="scheduleVideo"
                        name="schedule_video"
                        checked={formData.schedule_video}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>

                  {formData.schedule_video && (
                    <>
                      {/* ZOOM MEETING ID */}
                      <div>
                        <label
                          htmlFor="zoomMeetingId"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: '10px',
                            fontWeight: '600',
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Zoom Meeting ID
                        </label>
                        <input
                          type="text"
                          id="zoomMeetingId"
                          name="zoom_meeting_id"
                          value={formData.zoom_meeting_id}
                          readOnly
                          disabled
                          className="form-control"
                          placeholder="Will be generated after Zoom meeting is created"
                          style={{
                            width: '100%',
                            border: `1px solid ${theme.colors.borderColor}`,
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: '10px',
                            fontWeight: '100',
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            outline: 'none',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                            fontFamily: 'inherit',
                            cursor: 'not-allowed',
                          }}
                        />
                      </div>

                      {/* ZOOM JOIN URL */}
                      <div>
                        <label
                          htmlFor="zoomJoinUrl"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: '10px',
                            fontWeight: '600',
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Zoom Join URL
                        </label>
                        <input
                          type="text"
                          id="zoomJoinUrl"
                          name="zoom_join_url"
                          value={formData.zoom_join_url}
                          readOnly
                          disabled
                          className="form-control"
                          placeholder="Will be generated after Zoom meeting is created"
                          style={{
                            width: '100%',
                            border: `1px solid ${theme.colors.borderColor}`,
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: '10px',
                            fontWeight: '100',
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            outline: 'none',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                            fontFamily: 'inherit',
                            cursor: 'not-allowed',
                          }}
                        />
                      </div>

                      {/* ZOOM START URL */}
                      <div>
                        <label
                          htmlFor="zoomStartUrl"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: '10px',
                            fontWeight: '600',
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Zoom Start URL
                        </label>
                        <input
                          type="text"
                          id="zoomStartUrl"
                          name="zoom_start_url"
                          value={formData.zoom_start_url}
                          readOnly
                          disabled
                          className="form-control"
                          placeholder="Will be generated after Zoom meeting is created"
                          style={{
                            width: '100%',
                            border: `1px solid ${theme.colors.borderColor}`,
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: '10px',
                            fontWeight: '100',
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            outline: 'none',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                            fontFamily: 'inherit',
                            cursor: 'not-allowed',
                          }}
                        />
                      </div>

                      {/* ZOOM PASSWORD */}
                      <div>
                        <label
                          htmlFor="zoomPassword"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: '10px',
                            fontWeight: '600',
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Zoom Password
                        </label>
                        <input
                          type="text"
                          id="zoomPassword"
                          name="zoom_password"
                          value={formData.zoom_password}
                          readOnly
                          disabled
                          className="form-control"
                          style={{
                            width: '100%',
                            border: `1px solid ${theme.colors.borderColor}`,
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: '10px',
                            fontWeight: '100',
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            outline: 'none',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                            fontFamily: 'inherit',
                            cursor: 'not-allowed',
                          }}
                        />
                      </div>

                      {/* ZOOM CREATED AT */}
                      <div>
                        <label
                          htmlFor="zoomCreatedAt"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: '10px',
                            fontWeight: '600',
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Zoom Created At
                        </label>
                        <input
                          type="text"
                          id="zoomCreatedAt"
                          name="zoom_created_at"
                          value={formData.zoom_created_at}
                          readOnly
                          disabled
                          className="form-control"
                          placeholder="Will be filled after Zoom meeting is created"
                          style={{
                            width: '100%',
                            border: `1px solid ${theme.colors.borderColor}`,
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: '10px',
                            fontWeight: '100',
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            outline: 'none',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                            fontFamily: 'inherit',
                            cursor: 'not-allowed',
                          }}
                        />
                      </div>

                      {/* REGISTRANTS */}
                      <div>
                        <label
                          htmlFor="registrants"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: '10px',
                            fontWeight: '600',
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Registrants
                        </label>
                        <input
                          type="text"
                          id="registrants"
                          name="registrants"
                          value={formData.registrants}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder="Registrants"
                          disabled={isSubmitting}
                          className={`form-control ${errors.registrants ? 'is-invalid' : ''}`}
                          style={{
                            width: '100%',
                            border: `1px solid ${
                              errors.registrants
                                ? theme.colors.danger
                                : theme.colors.borderColor
                            }`,
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: '10px',
                            fontWeight: '100',
                            backgroundColor: theme.colors.bgPrimary,
                            color: '#666',
                            outline: 'none',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                            transition: 'border-color 0.3s ease',
                            opacity: isSubmitting ? 0.6 : 1,
                            fontFamily: 'inherit',
                          }}
                        />
                        {errors.registrants && (
                          <div
                            className="invalid-feedback"
                            style={{
                              color: theme.colors.danger,
                              display: 'block',
                              fontSize: '9px',
                              fontWeight: '500',
                              marginTop: '2px',
                            }}
                          >
                            {errors.registrants}
                          </div>
                        )}
                      </div>

                      {/* MEETING DURATION */}
                      <div>
                        <label
                          htmlFor="meetingDuration"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: '10px',
                            fontWeight: '600',
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Meeting Duration*
                        </label>
                        <input
                          type="number"
                          id="meetingDuration"
                          name="meeting_duration"
                          value={formData.meeting_duration}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          min="15"
                          required
                          disabled={isSubmitting}
                          className={`form-control ${
                            errors.meeting_duration ? 'is-invalid' : ''
                          }`}
                          style={{
                            width: '100%',
                            border: `1px solid ${
                              errors.meeting_duration
                                ? theme.colors.danger
                                : theme.colors.borderColor
                            }`,
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: '10px',
                            fontWeight: '100',
                            backgroundColor: theme.colors.bgPrimary,
                            color: '#666',
                            outline: 'none',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                            transition: 'border-color 0.3s ease',
                            opacity: isSubmitting ? 0.6 : 1,
                            fontFamily: 'inherit',
                          }}
                        />
                        {errors.meeting_duration && (
                          <div
                            className="invalid-feedback"
                            style={{
                              color: theme.colors.danger,
                              display: 'block',
                              fontSize: '9px',
                              fontWeight: '500',
                              marginTop: '2px',
                            }}
                          >
                            {errors.meeting_duration}
                          </div>
                        )}
                      </div>

                      {/* MEETING TIME */}
                      <div>
                        <label
                          htmlFor="meetingTime"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: '10px',
                            fontWeight: '600',
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Meeting Time*
                        </label>
                        <input
                          type="time"
                          id="meetingTime"
                          name="meeting_time"
                          value={formData.meeting_time}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          required
                          disabled={isSubmitting}
                          className={`form-control ${errors.meeting_time ? 'is-invalid' : ''}`}
                          style={{
                            width: '100%',
                            border: `1px solid ${
                              errors.meeting_time
                                ? theme.colors.danger
                                : theme.colors.borderColor
                            }`,
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: '10px',
                            fontWeight: '100',
                            backgroundColor: theme.colors.bgPrimary,
                            color: '#666',
                            outline: 'none',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                            transition: 'border-color 0.3s ease',
                            opacity: isSubmitting ? 0.6 : 1,
                            fontFamily: 'inherit',
                          }}
                        />
                        {errors.meeting_time && (
                          <div
                            className="invalid-feedback"
                            style={{
                              color: theme.colors.danger,
                              display: 'block',
                              fontSize: '9px',
                              fontWeight: '500',
                              marginTop: '2px',
                            }}
                          >
                            {errors.meeting_time}
                          </div>
                        )}
                      </div>

                      {/* MEETING TIMEZONE */}
                      <div>
                        <label
                          htmlFor="meetingTimezone"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: '10px',
                            fontWeight: '600',
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Meeting Timezone*
                        </label>
                        <select
                          id="meetingTimezone"
                          name="meeting_timezone"
                          value={formData.meeting_timezone}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          required
                          disabled={isSubmitting}
                          className={`form-control ${
                            errors.meeting_timezone ? 'is-invalid' : ''
                          }`}
                          style={{
                            width: '100%',
                            border: `1px solid ${
                              errors.meeting_timezone
                                ? theme.colors.danger
                                : theme.colors.borderColor
                            }`,
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: '10px',
                            fontWeight: '100',
                            backgroundColor: theme.colors.bgPrimary,
                            color: '#666',
                            outline: 'none',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                            transition: 'border-color 0.3s ease',
                            opacity: isSubmitting ? 0.6 : 1,
                            fontFamily: 'inherit',
                          }}
                        >
                          <option value="">Select Timezone</option>
                          <option value="America/New_York">
                            Eastern Time (US and Canada)
                          </option>
                          <option value="America/Chicago">
                            Central Time (US and Canada)
                          </option>
                          <option value="America/Denver">
                            Mountain Time (US and Canada)
                          </option>
                          <option value="America/Los_Angeles">
                            Pacific Time (US and Canada)
                          </option>
                          <option value="UTC">UTC</option>
                          <option value="Asia/Kolkata">India Standard Time</option>
                        </select>
                        {errors.meeting_timezone && (
                          <div
                            className="invalid-feedback"
                            style={{
                              color: theme.colors.danger,
                              display: 'block',
                              fontSize: '9px',
                              fontWeight: '500',
                              marginTop: '2px',
                            }}
                          >
                            {errors.meeting_timezone}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* ACTION BUTTONS */}
        <div
          style={{
            display: 'flex',
            flexDirection: window.innerWidth < 600 ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: window.innerWidth < 600 ? 'stretch' : 'center',
            padding: '12px',
            backgroundColor: theme.colors.bgPrimary,
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '8px',
              flexDirection: window.innerWidth < 600 ? 'column' : 'row',
              width: window.innerWidth < 600 ? '100%' : 'auto',
              position: 'relative',
            }}
          >
            <button
              onClick={handleCancel}
              disabled={isSubmitting}
              className="action-button"
              style={{
                background: theme.gradients.secondary,
                color: theme.colors.textWhite,
                fontSize: '9px',
                fontWeight: '600',
                padding: '8px 12px',
                borderRadius: '8px',
                border: 'none',
                transition: 'opacity 0.3s ease',
                boxShadow: 'none',
                width: window.innerWidth < 600 ? '100%' : 'auto',
                minWidth: '120px',
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              form="addDepositionForm"
              disabled={isLoading}
              className="action-button"
              style={{
                background: theme.gradients.primary,
                color: theme.colors.textWhite,
                fontSize: '9px',
                fontWeight: '600',
                padding: '8px 12px',
                borderRadius: '8px',
                border: 'none',
                transition: 'opacity 0.3s ease',
                boxShadow: 'none',
                width: window.innerWidth < 600 ? '100%' : 'auto',
                minWidth: '120px',
                opacity: isLoading ? 0.6 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
            </button>

            {isLoading && (
              <div
                style={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 9999,
                }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    border: `4px solid ${theme.colors.primary}`,
                    borderTop: '4px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
              </div>
            )}

            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddDeposition;